/* eslint-disable jsx-a11y/media-has-caption */
import {
  createRef,
  useCallback,
  useContext,
  useEffect,
  useState,
} from 'react';
import { Button, Typography } from '@mui/material';
import BlockComponentText from '../../../models/BlockComponentValues/BlockComponentText';
import BlockComponentVideo from '../../../models/BlockComponentValues/BlockComponentVideo';
import ModalComponent from '../../ModalComponent';
import VideoModal from './VideoModal';
import EditableText from '../../Form/EditableText';
import ColorPicker from '../../Pickers/ColorPicker/ColorPicker';
import { VideoContext } from '../../../contexts/VideoContext';

interface VideoSettingsModalProps {
  title: BlockComponentText;
  video: BlockComponentVideo;
  showModal: boolean;
  setShowModal: (show: boolean) => void;
  onConfirm: (
    title: string,
    video: string,
    backgroundColor: string,
    loop: boolean,
    autoplay: boolean,
    muted: boolean,
    staticBackground: boolean,
  ) => void;
}

function VideoSettingsModal({
  title,
  video,
  showModal,
  setShowModal,
  onConfirm,
}: VideoSettingsModalProps) {
  const { videos } = useContext(VideoContext);
  const videoRef = createRef<HTMLVideoElement>();

  const [selectedFile, setSelectedFile] = useState(videos.find((v) => v.src === video.src)?.file_name || '');

  const [tmpTitle, setTmpTitle] = useState(title.value);
  const [tmpBackgroundColor, setTmpBackgroundColor] = useState(title.settings.backgroundColor);
  const [tmpVideo, setTmpVideo] = useState(video.src);
  const [tmpLoop, setTmpLoop] = useState(video.settings.loop);
  const [tmpAutoplay, setTmpAutoplay] = useState(video.settings.autoplay);
  const [tmpMuted, setTmpMuted] = useState(video.settings.muted);
  const [tmpStatic, setTmpStatic] = useState(video.settings.staticBackground);

  const reloadVideo = useCallback(() => {
    if (videoRef.current) videoRef.current.load();
  }, [videoRef]);

  const handleConfirm = () => {
    onConfirm(
      tmpTitle,
      tmpVideo,
      tmpBackgroundColor,
      tmpLoop,
      tmpAutoplay,
      tmpMuted,
      tmpStatic,
    );

    setShowModal(false);
  };

  const handleDefaultVideo = () => {
    setTmpVideo('/videos/video.mp4');
    reloadVideo();
  };

  useEffect(() => {
    setSelectedFile(videos.find((v) => v.src === tmpVideo)?.file_name || '');
  }, [tmpVideo, videos]);

  useEffect(() => {
    setTmpVideo(video.src);
  }, [showModal]);

  return (
    <ModalComponent
      show={showModal}
      closeModal={() => setShowModal(false)}
      onConfirm={handleConfirm}
      size="xl"
      title="動画設定"
      confirmButtonText="保存する"
      cancelButtonText="閉じる"
      centered
      style={{
        zIndex: 1051,
      }}
    >
      <div className="row">
        <div className="col-xl-5">
          <div className="d-flex flex-column">
            <div>
              <Typography variant="subtitle2" className="text-bold" color="#495057">
                表示テキスト
              </Typography>
              <div className="w-100">
                <div className="editable-text-container">
                  <EditableText
                    text={tmpTitle}
                    isEditable
                    updateHandler={(value) => {
                      setTmpTitle(value);
                    }}
                  />
                </div>
              </div>
            </div>

            <div>
              <Typography variant="subtitle2" className="text-bold" color="#495057">
                背景色
              </Typography>

              <ColorPicker
                value={tmpBackgroundColor}
                onPickColor={(color) => setTmpBackgroundColor(color)}
              />
            </div>

            <div className="mt-4 d-flex flex-column gap-3">
              <div>
                {/* sound */}
                <Typography variant="subtitle2" className="text-bold" color="#495057">
                  音声
                </Typography>
                {/* checkbox */}
                <div className="d-flex align-items-center gap-2">
                  <input
                    id="loop"
                    type="checkbox"
                    name="loop"
                    value={tmpLoop}
                    checked={tmpLoop}
                    onChange={(e) => setTmpLoop(e.target.checked)}
                  />
                  <label htmlFor="loop">ループ再生</label>
                  <input
                    id="autoplay"
                    type="checkbox"
                    name="autoplay"
                    value={tmpAutoplay}
                    checked={tmpAutoplay}
                    onChange={(e) => setTmpAutoplay(e.target.checked)}
                  />
                  <label htmlFor="autoplay">自動再生</label>
                  <input
                    id="muted"
                    type="checkbox"
                    name="muted"
                    value={tmpMuted}
                    checked={tmpMuted}
                    onChange={(e) => setTmpMuted(e.target.checked)}
                  />
                  <label htmlFor="muted">ミュート</label>
                </div>
              </div>

              <div>
                <Typography variant="subtitle2" className="text-bold" color="#495057">
                  動画設定
                </Typography>

                {/* checkbox to see if video should be static in background */}
                <div className="d-flex align-items-center gap-2">
                  <input
                    id="static"
                    type="checkbox"
                    name="static"
                    value={tmpStatic}
                    checked={tmpStatic}
                    onChange={(e) => setTmpStatic(e.target.checked)}
                  />
                  <label htmlFor="static">背景固定</label>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className="col-xl-7">
          <div className="d-flex align-items-center justify-content-between gap-2 mb-3">
            <div className="d-flex flex-column w-100">
              <div className="d-flex align-items-center gap-2">
                {/* video modal */}
                <video
                  ref={videoRef}
                  controls
                  width="100%"
                  height="100%"
                >
                  <source src={tmpVideo} type="video/mp4" />
                </video>
              </div>

              <div className="d-flex align-items-center justify-content-between gap-1">
                <div className="d-flex align-items-center gap-1">
                  <VideoModal
                    currentFilename={selectedFile}
                    editHandler={(video) => {
                      setTmpVideo(video.src);
                      setSelectedFile(video.file_name);
                      reloadVideo();
                    }}
                  />
                </div>
                <Button
                  variant="contained"
                  color="primary"
                  size="small"
                  onClick={() => handleDefaultVideo()}
                  sx={{ marginTop: '10px', marginBottom: '10px', minWidth: 127 }}
                >
                  初期画像に戻す
                </Button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </ModalComponent>
  );
}

export default VideoSettingsModal;
